import { MutableRefObject, useEffect, useRef } from 'react';

interface IUseClickOutsideOptions {
  /**
   * Treat clicks inside `#portal-root` as clicks inside the component. Only for
   * components that portal part of their own UI there (e.g. `Select`'s
   * dropdown) — otherwise an unrelated portal would stop dismissing this one.
   */
  ignorePortalRoot?: boolean;
}

export function useClickOutside<Element = any>(
  handler: () => void,
  active = true,
  { ignorePortalRoot = false }: IUseClickOutsideOptions = {}
): MutableRefObject<Element> {
  const domNode = useRef(null);

  // Keep the latest handler without re-registering the listener on every render.
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!active) return undefined;

    const maybeHandler = (event: any): void => {
      if (domNode.current?.contains(event.target)) return;

      if (event.target?.id?.includes?.('select-option')) return;

      if (ignorePortalRoot) {
        const portalRoot = document.getElementById('portal-root');

        if (portalRoot?.contains(event.target)) return;
      }

      handlerRef.current?.();
    };

    document.addEventListener('mousedown', maybeHandler);

    return () => {
      document.removeEventListener('mousedown', maybeHandler);
    };
  }, [active, ignorePortalRoot]);

  return domNode;
}
